import {
    ArrowBracketRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { NavLink } from "react-router-dom";

import type { YaasTopBarUser } from "./YaasTopBar";

export interface NavItem {
    label: string;
    path: string;
    icon?: React.ReactNode;
    /** Notification count — renders a square ember badge over the icon for
     *  anything needing attention (e.g. low-funds sources). Hidden when 0. */
    badge?: number;
    /** Exact-match the route (NavLink `end`). Set for index paths that are a
     *  prefix of sibling routes (e.g. `/wu` under `/wu/programs`) so they
     *  don't stay highlighted on every child page. Defaults to true for `/`. */
    end?: boolean;
}

export interface NavDivider {
    type: "divider";
}

/** A small label over a group of items, naming the role the group serves
 *  (e.g. the client console's "Recipient" / "Distributor"). Hidden when the
 *  rail is collapsed, where there is no room for text. */
export interface NavSection {
    type: "section";
    label: string;
}

export type NavEntry = NavItem | NavDivider | NavSection;

interface YaasSidebarProps {
    navItems: NavEntry[];
    /**
     * Items pinned to the bottom of the sidebar — rendered in the same
     * bottom block as the Collapse button and the user profile.
     */
    bottomNavItems?: NavEntry[];
    /** Optional non-navigation content rendered above the footer block. */
    sidebarAccessory?: React.ReactNode;
    user?: YaasTopBarUser;
    onLogout?: () => void;
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
    /** Tailwind width class for the expanded rail. Defaults to `w-40`. */
    expandedWidthClass?: string;
    /**
     * `rail` — the desktop in-flow column that collapses to an icon
     * rail (default). `drawer` — the mobile off-canvas variant: always
     * shows labels (it's only visible when opened), fills its fixed
     * parent, and drops the Collapse affordance.
     */
    variant?: "rail" | "drawer";
    /**
     * Which side the drawer sits on — sets its inner divider edge
     * (`right` → `border-l`, `left` → `border-r`, `bottom` → `border-t`).
     * Only relevant for the `drawer` variant; ignored by the rail. Defaults
     * to `left`.
     */
    drawerSide?: "left" | "right" | "bottom";
    /**
     * Fired when a nav row is activated. The drawer passes a close
     * handler so tapping an item dismisses the overlay.
     */
    onNavigate?: () => void;
}

function isDivider(entry: NavEntry): entry is NavDivider {
    return "type" in entry && entry.type === "divider";
}

function isSection(entry: NavEntry): entry is NavSection {
    return "type" in entry && entry.type === "section";
}

export function YaasSidebar({
    navItems,
    bottomNavItems,
    sidebarAccessory,
    user,
    onLogout,
    collapsed = false,
    onToggleCollapsed,
    expandedWidthClass = "w-40",
    variant = "rail",
    drawerSide = "left",
    onNavigate
}: YaasSidebarProps): React.ReactElement {
    const displayName = user?.fullName ?? user?.email ?? "User";
    const avatarLetter =
        (user?.fullName ?? user?.email ?? "U").charAt(0).toUpperCase() || "U";

    const isDrawer = variant === "drawer";
    // The drawer is only ever on-screen when opened, so it always shows
    // full labels; `collapsed` is a desktop-rail concept.
    const effectiveCollapsed = isDrawer ? false : collapsed;
    // Drawer's inner divider rides the edge facing the content. The bottom
    // sheet is full-width and sits flush under the top bar's own border, so it
    // needs no edge of its own.
    const drawerBorder =
        drawerSide === "bottom"
            ? ""
            : drawerSide === "right"
              ? "border-l"
              : "border-r";
    const rootClass = isDrawer
        ? `w-full h-full overflow-y-auto flex flex-col bg-surface-ground ${drawerBorder} border-line-base`
        : `${collapsed ? "w-12" : expandedWidthClass} flex flex-col shrink-0 bg-surface-ground border-r border-line-base transition-all duration-200`;

    return (
        <nav className={rootClass}>
            <div className="flex-1 pb-1">
                {navItems.map((entry, i) => {
                    if (isDivider(entry)) {
                        return (
                            <div
                                key={`div-${i}`}
                                className="border-t border-line-base"
                            />
                        );
                    }
                    if (isSection(entry)) {
                        // Left edge matches the rows' px-3 so the label sits
                        // over the icons.
                        return effectiveCollapsed ? null : (
                            <Text
                                key={`section-${entry.label}`}
                                variant="label"
                                render={<p />}
                                className="px-3 pt-3 pb-1 text-foreground-muted"
                            >
                                {entry.label}
                            </Text>
                        );
                    }
                    return (
                        <NavEntryLink
                            key={entry.path}
                            item={entry}
                            collapsed={effectiveCollapsed}
                            onNavigate={onNavigate}
                        />
                    );
                })}
            </div>

            {sidebarAccessory && !effectiveCollapsed ? (
                <div className="border-t border-line-base">
                    {sidebarAccessory}
                </div>
            ) : null}

            {/* ── Bottom block — Collapse, bottom nav items, user profile ── */}
            <div className="flex flex-col">
                {onToggleCollapsed && !isDrawer && (
                    <button
                        onClick={onToggleCollapsed}
                        type="button"
                        className="group relative flex items-center gap-2 py-2 font-sans font-normal text-sm md:text-base transition-colors text-foreground-imprint hover:text-foreground-base hover:bg-surface-sunken border-t border-t-surface-ground border-b border-b-surface-ground hover:border-t-line-base hover:border-b-line-base px-3"
                        aria-label={
                            collapsed ? "Expand sidebar" : "Collapse sidebar"
                        }
                    >
                        <NavIconSlot>
                            {collapsed ? (
                                <ChevronRightIcon className="size-4" />
                            ) : (
                                <ChevronLeftIcon className="size-4" />
                            )}
                        </NavIconSlot>
                        <NavLabel collapsed={collapsed}>Collapse</NavLabel>
                        {collapsed && <NavTooltip label="Expand" />}
                    </button>
                )}

                {bottomNavItems &&
                    bottomNavItems.length > 0 &&
                    bottomNavItems.map((entry, i) => {
                        if (isDivider(entry)) {
                            return (
                                <div
                                    key={`bdiv-${i}`}
                                    className="border-t border-line-base"
                                />
                            );
                        }
                        if (isSection(entry)) {
                            return effectiveCollapsed ? null : (
                                <Text
                                    key={`bsection-${entry.label}`}
                                    variant="label"
                                    render={<p />}
                                    className="px-3 pt-3 pb-1 text-foreground-muted"
                                >
                                    {entry.label}
                                </Text>
                            );
                        }
                        return (
                            <NavEntryLink
                                key={entry.path}
                                item={entry}
                                collapsed={effectiveCollapsed}
                                onNavigate={onNavigate}
                            />
                        );
                    })}

                {user && (
                    <>
                        <NavLink
                            to="/settings"
                            onClick={onNavigate}
                            className={({ isActive }) =>
                                `group relative flex items-center gap-2 py-2 min-w-0 transition-colors duration-150 border-t border-b px-3 ${
                                    isActive
                                        ? "bg-surface-sunken text-foreground-base border-t-accent-base border-b-line-base"
                                        : "border-t-surface-ground border-b-surface-ground text-foreground-muted hover:text-foreground-base hover:bg-surface-sunken hover:border-t-line-base hover:border-b-line-base"
                                }`
                            }
                            title={effectiveCollapsed ? undefined : "Settings"}
                        >
                            <NavIconSlot>
                                <div className="w-5 h-5 bg-surface-inverse-raised flex items-center justify-center">
                                    <Text
                                        variant="label"
                                        className="text-foreground-inverse"
                                    >
                                        {avatarLetter}
                                    </Text>
                                </div>
                            </NavIconSlot>
                            <NavLabel
                                collapsed={effectiveCollapsed}
                                className="font-sans font-normal text-sm md:text-base truncate"
                            >
                                {displayName}
                            </NavLabel>
                            {effectiveCollapsed && (
                                <NavTooltip label={displayName} />
                            )}
                        </NavLink>
                        {onLogout && (
                            <button
                                onClick={onLogout}
                                type="button"
                                aria-label="Log out"
                                className="group relative flex items-center gap-2 py-2 font-sans font-normal text-sm md:text-base text-foreground-imprint hover:text-foreground-base hover:bg-surface-sunken transition-colors duration-150 border-t border-t-surface-ground border-b border-b-surface-ground hover:border-t-line-base hover:border-b-line-base px-3"
                            >
                                <NavIconSlot>
                                    <ArrowBracketRightIcon />
                                </NavIconSlot>
                                <NavLabel collapsed={effectiveCollapsed}>
                                    Log out
                                </NavLabel>
                                {effectiveCollapsed && (
                                    <NavTooltip label="Log out" />
                                )}
                            </button>
                        )}
                    </>
                )}

                {/* Drawer-only identity footer — the client/issuer lockup that
                    lives in the top bar on desktop moves here on mobile. Gated
                    on `issuerName`, which only the client console supplies. */}
                {isDrawer && user?.issuerName && (
                    <div className="border-t border-line-base px-3 py-3">
                        <Text
                            variant="small"
                            render={<span />}
                            className="block truncate text-foreground-base"
                        >
                            {user.orgName}
                        </Text>
                        <Text
                            variant="small"
                            render={<span />}
                            className="block truncate text-foreground-muted"
                        >
                            Distributor:{" "}
                            <span className="text-foreground-base">
                                {user.issuerName}
                            </span>
                        </Text>
                    </div>
                )}
            </div>
        </nav>
    );
}

function NavEntryLink({
    item,
    collapsed,
    onNavigate
}: {
    item: NavItem;
    collapsed: boolean;
    onNavigate?: () => void;
}): React.ReactElement {
    const badge = item.badge ?? 0;
    return (
        <NavLink
            to={item.path}
            end={item.end ?? item.path === "/"}
            onClick={onNavigate}
            className={({ isActive }) =>
                `group relative flex items-center gap-2 py-2 font-sans font-normal text-sm md:text-base transition-colors duration-150 border-t border-b px-3 ${
                    isActive
                        ? "bg-surface-sunken text-foreground-base font-bold border-t-accent-base border-b-line-base"
                        : "border-t-surface-ground border-b-surface-ground text-foreground-imprint hover:text-foreground-base hover:bg-surface-sunken hover:border-t-line-base hover:border-b-line-base"
                }`
            }
        >
            {item.icon ? (
                <span className="relative shrink-0">
                    <NavIconSlot>{item.icon}</NavIconSlot>
                    {/* Collapsed rail has no room for a trailing badge, so it
                        rides the icon's top-right corner instead. */}
                    {badge > 0 && collapsed ? (
                        <span className="absolute -top-1 -right-1">
                            <NavBadge count={badge} size="sm" />
                        </span>
                    ) : null}
                </span>
            ) : null}
            <NavLabel collapsed={collapsed}>{item.label}</NavLabel>
            {/* Expanded: badge sits at the right end of the row. */}
            {badge > 0 && !collapsed ? (
                <NavBadge count={badge} size="md" className="ml-auto" />
            ) : null}
            {collapsed && <NavTooltip label={item.label} />}
        </NavLink>
    );
}

/**
 * Square notification badge — error-inverse fill (the brand "alert" red) with
 * foreground-base text, square (not pill) to match the design language. `md`
 * rides the right end of an expanded row (roomy, easy to read); `sm` rides the
 * icon corner in the collapsed rail. Positioning is left to the caller. Caps
 * at "99+".
 */
function NavBadge({
    count,
    size = "md",
    className = ""
}: {
    count: number;
    size?: "sm" | "md";
    className?: string;
}): React.ReactElement {
    // Count glyphs sit below the smallest type token — intentional so the
    // digits stay legible inside the square without dictating its height.
    const dims =
        size === "sm"
            ? "h-4 min-w-4 px-1 text-[11px]"
            : "h-5 min-w-5 px-1.5 text-[13px]";
    return (
        <span
            className={`flex items-center justify-center bg-error-inverse font-numeric font-bold leading-none text-foreground-base ${dims} ${className}`}
        >
            {count > 99 ? "99+" : count}
        </span>
    );
}

/**
 * Standard icon slot — 24×24 container, centered. Any child (SVG icon,
 * small avatar, etc.) is centered inside. Keeps every sidebar row's
 * icon column the same width so text alignment stays stable across
 * items whether the icon is 16×16, 20×20, or full-24.
 */
function NavIconSlot({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <span className="shrink-0 w-6 h-6 flex items-center justify-center">
            {children}
        </span>
    );
}

/**
 * Animated label for sidebar rows. Only renders when the sidebar is
 * expanded. On expand the text pans in from the left and scales up,
 * with a 150ms delay so the sidebar's own width transition starts
 * first (sidebar expands in 200ms; text joins at 150ms and runs out
 * to 350ms). On collapse the label disappears instantly so it can't
 * overflow the shrinking sidebar.
 */
function NavLabel({
    collapsed,
    children,
    className = ""
}: {
    collapsed: boolean;
    children: React.ReactNode;
    className?: string;
}): React.ReactElement {
    return (
        <AnimatePresence initial={false}>
            {!collapsed && (
                <motion.span
                    initial={{ opacity: 0, x: -8, scale: 0.75 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{
                        opacity: 0,
                        x: -8,
                        scale: 0.75,
                        transition: { duration: 0 }
                    }}
                    transition={{ duration: 0.2, delay: 0.15 }}
                    className={`whitespace-nowrap origin-left ${className}`}
                >
                    {children}
                </motion.span>
            )}
        </AnimatePresence>
    );
}

/** Tooltip that appears on hover when the sidebar is collapsed. */
function NavTooltip({ label }: { label: string }): React.ReactElement {
    return (
        <Text
            variant="small"
            render={<span />}
            className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-surface-inverse-raised text-foreground-inverse rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity delay-500 z-50"
        >
            {label}
        </Text>
    );
}
